import * as THREE from 'three';
import {
  makeBrickTexture,
  makeConcreteTexture,
  makeSandTexture,
  makeWoodTexture,
  makeMetalTexture,
  makeTrimTexture,
  makeDarkWallTexture,
  makeRustyTexture,
} from './scene-textures';
import { generateNormalMap } from './texture-normalmap';
import type { PBRTextureLoader, PBRTextureSet } from './texture-loader';

/** Generate a roughness map from a canvas (darker = smoother, lighter = rougher) */
function generateRoughnessMap(sourceCanvas: HTMLCanvasElement, baseRoughness = 0.5, variation = 0.3): THREE.CanvasTexture {
  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  const srcCtx = sourceCanvas.getContext('2d')!;
  const srcData = srcCtx.getImageData(0, 0, w, h).data;

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const outCtx = out.getContext('2d')!;
  const outImg = outCtx.createImageData(w, h);

  for (let i = 0; i < w * h; i++) {
    const r = srcData[i * 4], g = srcData[i * 4 + 1], b = srcData[i * 4 + 2];
    const lum = (r * 0.299 + g * 0.587 + b * 0.114) / 255;
    // Invert and scale: mortar lines (dark) → smoother, brick faces → rougher
    const roughness = Math.min(1, Math.max(0, baseRoughness + (lum - 0.5) * variation));
    const val = Math.floor(roughness * 255);
    const idx = i * 4;
    outImg.data[idx] = val;
    outImg.data[idx + 1] = val;
    outImg.data[idx + 2] = val;
    outImg.data[idx + 3] = 255;
  }

  outCtx.putImageData(outImg, 0, 0);
  const tex = new THREE.CanvasTexture(out);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

// ---- Phase 6: Hex Tiling (Anti-Repetition) --------------------------------

const HEX_TILING_VERT_INJECT = `
// Hex tiling: pass through world UV for hex offset in fragment
varying vec2 vHexUV;
`;

const HEX_TILING_VERT_MAIN_INJECT = `
vHexUV = uv;
`;

const HEX_TILING_FRAG_UNIFORMS = `
varying vec2 vHexUV;
`;

// GLSL hex tiling function injected before map_fragment
const HEX_TILING_FRAG_FUNCTIONS = `
vec4 hexTileCell(vec2 uv) {
  vec2 r = vec2(1.0, 1.732050808);
  vec2 h = r * 0.5;
  vec2 a = mod(uv, r) - h;
  vec2 b = mod(uv - h, r) - h;
  vec2 gv = dot(a, a) < dot(b, b) ? a : b;
  vec2 id = uv - gv;
  return vec4(gv, id);
}

// Smooth blend weight for hex cell (peaks at center, zero at boundary)
float hexWeight(vec2 p) {
  float d = max(abs(p.x) * 1.1547 + abs(p.y), abs(p.y) * 2.0) * 0.5773;
  return smoothstep(0.5, 0.2, d);
}

// Hash-based rotation angle for a cell id
float cellRotation(vec2 id) {
  float h = fract(sin(dot(id, vec2(127.1, 311.7))) * 43758.5453);
  return h * 6.2831853;
}

// Sample map with hex tiling anti-repetition (3 samples blended)
vec4 sampleHexTiled(sampler2D tex, vec2 uv) {
  // Scale UV into hex grid space (each cell ~1 unit wide)
  vec2 hexUV = uv * 3.46410161; // 2*sqrt(3) spacing

  vec4 c0 = hexTileCell(hexUV);
  vec2 localUV = c0.xy; // local coords within hex cell
  vec2 cellID  = c0.zw; // integer cell center

  // Sample from 3 neighbouring cells to ensure coverage
  vec2 r = vec2(1.0, 1.732050808);
  vec2 h = r * 0.5;
  vec2 offsets[3];
  offsets[0] = vec2(0.0, 0.0);
  offsets[1] = vec2(r.x, 0.0);
  offsets[2] = vec2(h.x, h.y);

  vec4 colBlend = vec4(0.0);
  float wSum = 0.0;

  for (int i = 0; i < 3; i++) {
    vec2 sampleHex = hexUV - offsets[i];
    vec4 cell = hexTileCell(sampleHex);
    vec2 sID = cell.zw + offsets[i];

    float rot = cellRotation(sID);
    float cs = cos(rot), ss = sin(rot);
    // Rotate local UV to break tiling pattern
    vec2 rotUV = uv + vec2(cs * cell.x - ss * cell.y, ss * cell.x + cs * cell.y) * 0.289;

    float w = hexWeight(cell.xy);
    colBlend += texture2D(tex, rotUV) * w;
    wSum += w;
  }

  return colBlend / max(wSum, 0.001);
}
`;

// Replace the map_fragment include to call hex sampler
const HEX_MAP_FRAGMENT_REPLACE = `
#ifdef USE_MAP
  vec4 sampledDiffuseColor = sampleHexTiled(map, vMapUv);
  #ifdef DECODE_VIDEO_TEXTURE
    // inline sRGB decode for video — keep existing behavior
    sampledDiffuseColor = vec4(mix(pow(sampledDiffuseColor.rgb * 0.9478672986 + vec3(0.0521327014), vec3(2.4)), sampledDiffuseColor.rgb * 0.0773993808, vec3(lessThanEqual(sampledDiffuseColor.rgb, vec3(0.04045)))), sampledDiffuseColor.w);
  #endif
  diffuseColor *= sampledDiffuseColor;
#endif
`;

/**
 * Apply hex tiling anti-repetition to a MeshStandardMaterial's color map.
 * Uses onBeforeCompile shader injection — no npm package required.
 */
export function applyHexTiling(material: THREE.MeshStandardMaterial): void {
  if (!material.map) return;

  material.onBeforeCompile = (shader) => {
    // Inject varying into vertex shader
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <uv_pars_vertex>',
        '#include <uv_pars_vertex>\n' + HEX_TILING_VERT_INJECT,
      )
      .replace(
        '#include <uv_vertex>',
        '#include <uv_vertex>\nvHexUV = uv;',
      );

    // Inject uniforms + functions into fragment shader
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <map_pars_fragment>',
        '#include <map_pars_fragment>\n' + HEX_TILING_FRAG_UNIFORMS + HEX_TILING_FRAG_FUNCTIONS,
      )
      .replace(
        '#include <map_fragment>',
        HEX_MAP_FRAGMENT_REPLACE,
      );
  };

  material.customProgramCacheKey = () => 'hex_tiling_v1';
  material.needsUpdate = true;
}

// ---- Phase 6: Procedural Detail Normal Textures ---------------------------

/** Fine concrete/plaster grain normal map (256x256) */
export function generateConcreteGrainNormal(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(size, size);

  // Multi-octave fine noise heightmap
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let h = 0;
      // 4 octaves of pseudo-random noise
      h += Math.sin(x * 0.31 + y * 0.17) * 0.25;
      h += Math.sin(x * 0.73 - y * 0.51) * 0.15;
      h += Math.sin(x * 1.47 + y * 1.13) * 0.07;
      h += Math.sin(x * 3.11 - y * 2.97) * 0.03;
      // Add grain-like speckle
      const speckle = Math.sin(x * 17.3 + y * 13.7) * Math.cos(x * 7.11 - y * 11.3) * 0.05;
      h = (h + speckle + 0.5);
      const idx = (y * size + x) * 4;
      const val = Math.min(255, Math.max(0, Math.floor(h * 255)));
      img.data[idx] = val;
      img.data[idx + 1] = val;
      img.data[idx + 2] = val;
      img.data[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  const tex = new THREE.CanvasTexture(generateNormalMap(canvas, 1.5).image as HTMLCanvasElement);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

/** Coarse rock/stone grain normal map (256x256) */
export function generateRockGrainNormal(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(size, size);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Coarser, lower-frequency noise
      let h = 0;
      h += Math.sin(x * 0.09 + y * 0.07) * 0.35;
      h += Math.sin(x * 0.23 - y * 0.19) * 0.25;
      h += Math.sin(x * 0.51 + y * 0.47) * 0.15;
      h += Math.sin(x * 1.13 - y * 0.97) * 0.07;
      // Crevice-like ridges
      const ridge = Math.abs(Math.sin(x * 0.051 + y * 0.037)) * 0.1;
      h = h + ridge + 0.5;
      const idx = (y * size + x) * 4;
      const val = Math.min(255, Math.max(0, Math.floor(h * 255)));
      img.data[idx] = val;
      img.data[idx + 1] = val;
      img.data[idx + 2] = val;
      img.data[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  const normalCanvas = generateNormalMap(canvas, 2.5).image as HTMLCanvasElement;
  const tex = new THREE.CanvasTexture(normalCanvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

/** Directional metal scratch normal map (256x256) */
export function generateMetalScratchNormal(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(size, size);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Horizontal scratches — high-frequency in X, low-frequency in Y
      let h = 0;
      h += Math.sin(y * 2.17 + Math.sin(x * 0.031) * 5) * 0.3;
      h += Math.sin(y * 5.73 + Math.sin(x * 0.071) * 3) * 0.15;
      h += Math.sin(y * 11.3 + Math.cos(x * 0.11) * 2) * 0.07;
      // Occasional deeper scratch
      const deepScratch = Math.abs(Math.sin(y * 0.43 + x * 0.013)) > 0.97 ? 0.15 : 0.0;
      h = h + deepScratch + 0.5;
      const idx = (y * size + x) * 4;
      const val = Math.min(255, Math.max(0, Math.floor(h * 255)));
      img.data[idx] = val;
      img.data[idx + 1] = val;
      img.data[idx + 2] = val;
      img.data[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  const normalCanvas = generateNormalMap(canvas, 2.0).image as HTMLCanvasElement;
  const tex = new THREE.CanvasTexture(normalCanvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

/** Parallel wood grain normal map with variation (256x256) */
export function generateWoodGrainNormal(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(size, size);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Grain runs along X axis with slight waviness
      const wave = Math.sin(x * 0.07) * 8 + Math.sin(x * 0.13) * 4;
      const grain = Math.sin((y + wave) * 0.8) * 0.3
        + Math.sin((y + wave * 0.5) * 1.9) * 0.12
        + Math.sin((y + wave * 0.3) * 4.1) * 0.05;
      // Knot-like feature
      const dx = x - size * 0.6, dy = y - size * 0.4;
      const knot = Math.exp(-(dx * dx + dy * dy) * 0.001) * 0.1;
      const h = grain + knot + 0.5;
      const idx = (y * size + x) * 4;
      const val = Math.min(255, Math.max(0, Math.floor(h * 255)));
      img.data[idx] = val;
      img.data[idx + 1] = val;
      img.data[idx + 2] = val;
      img.data[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  const normalCanvas = generateNormalMap(canvas, 2.0).image as HTMLCanvasElement;
  const tex = new THREE.CanvasTexture(normalCanvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

// Cached detail normal textures (generated once)
let _concreteGrain: THREE.CanvasTexture | null = null;
let _rockGrain: THREE.CanvasTexture | null = null;
let _metalScratch: THREE.CanvasTexture | null = null;
let _woodGrain: THREE.CanvasTexture | null = null;

function getConcreteGrain(): THREE.CanvasTexture {
  if (!_concreteGrain) _concreteGrain = generateConcreteGrainNormal();
  return _concreteGrain;
}
function getRockGrain(): THREE.CanvasTexture {
  if (!_rockGrain) _rockGrain = generateRockGrainNormal();
  return _rockGrain;
}
function getMetalScratch(): THREE.CanvasTexture {
  if (!_metalScratch) _metalScratch = generateMetalScratchNormal();
  return _metalScratch;
}
function getWoodGrain(): THREE.CanvasTexture {
  if (!_woodGrain) _woodGrain = generateWoodGrainNormal();
  return _woodGrain;
}

// ---- Phase 6: Detail Normal Map Injection ---------------------------------

/**
 * Add a secondary detail normal map that fades out with camera distance.
 * Injected via onBeforeCompile — blends with the primary normal.
 *
 * @param material         Target MeshStandardMaterial
 * @param detailNormalTex  256x256 procedural normal texture
 * @param detailScale      UV scale multiplier for the detail normal (e.g. 8.0)
 * @param detailStrength   Blend strength [0..1]
 * @param fadeStart        Camera distance at which detail starts fading (world units)
 * @param fadeEnd          Camera distance at which detail is fully gone
 */
export function addDetailNormal(
  material: THREE.MeshStandardMaterial,
  detailNormalTex: THREE.Texture,
  detailScale = 8.0,
  detailStrength = 0.5,
  fadeStart = 4.0,
  fadeEnd = 12.0,
): void {
  // Compose with any existing onBeforeCompile (e.g. hex tiling)
  const prevOnBeforeCompile = material.onBeforeCompile;
  const prevCacheKey = material.customProgramCacheKey;

  material.onBeforeCompile = (shader, renderer) => {
    // Run any previously registered compiler (hex tiling, etc.)
    if (prevOnBeforeCompile) prevOnBeforeCompile(shader, renderer);

    // Add uniforms
    shader.uniforms['detailNormalMap'] = { value: detailNormalTex };
    shader.uniforms['detailNormalScale'] = { value: detailScale };
    shader.uniforms['detailNormalStrength'] = { value: detailStrength };
    shader.uniforms['detailFadeStart'] = { value: fadeStart };
    shader.uniforms['detailFadeEnd'] = { value: fadeEnd };

    // Inject uniform declarations at the top of the fragment shader
    const detailUniforms = `
uniform sampler2D detailNormalMap;
uniform float detailNormalScale;
uniform float detailNormalStrength;
uniform float detailFadeStart;
uniform float detailFadeEnd;
`;

    // Inject blend logic AFTER the normal_fragment_maps include
    const detailBlendGLSL = `
// Detail normal fade based on fragment depth (camera distance approximation)
float detailDist = length(vViewPosition);
float detailFade = 1.0 - smoothstep(detailFadeStart, detailFadeEnd, detailDist);

if (detailFade > 0.001) {
  // Sample detail normal at higher frequency
  vec2 detailUV = vMapUv * detailNormalScale;
  vec3 detailNorm = texture2D(detailNormalMap, detailUV).xyz * 2.0 - 1.0;
  // Reorient blend: combine primary normal (already in tangent space) with detail
  vec3 primaryN = normal;
  vec3 blended = normalize(vec3(
    primaryN.xy + detailNorm.xy * detailNormalStrength * detailFade,
    primaryN.z
  ));
  normal = blended;
}
`;

    shader.fragmentShader = shader.fragmentShader
      .replace(
        'uniform float opacity;',
        'uniform float opacity;\n' + detailUniforms,
      )
      .replace(
        '#include <normal_fragment_maps>',
        '#include <normal_fragment_maps>\n' + detailBlendGLSL,
      );
  };

  material.customProgramCacheKey = () => {
    const prev = prevCacheKey ? prevCacheKey() : '';
    return prev + `_detail_normal_s${detailScale}_str${detailStrength}`;
  };
  material.needsUpdate = true;
}

// ---- Phase 6: Procedural AO Map Generation --------------------------------

export interface AOConfig {
  /** AO intensity (0..1) stored on material.aoMapIntensity */
  intensity: number;
  /** Blur radius in pixels for the edge-detection pass */
  blurRadius?: number;
  /** Edge sensitivity: higher = more AO in fine crevices */
  edgeSensitivity?: number;
}

/**
 * Generate a procedural AO texture by running edge-detection + gaussian blur
 * on the source color canvas. Dark edges / crevices receive more occlusion.
 *
 * Returns a CanvasTexture ready to assign to material.aoMap.
 */
export function generateProceduralAO(
  sourceCanvas: HTMLCanvasElement,
  config: AOConfig,
): THREE.CanvasTexture {
  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  const srcCtx = sourceCanvas.getContext('2d')!;
  const srcData = srcCtx.getImageData(0, 0, w, h).data;

  // Step 1: Convert to grayscale
  const gray = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) {
    const r = srcData[i * 4], g = srcData[i * 4 + 1], b = srcData[i * 4 + 2];
    gray[i] = (r * 0.299 + g * 0.587 + b * 0.114) / 255;
  }

  // Step 2: Sobel edge magnitude
  const edgeSens = config.edgeSensitivity ?? 3.0;
  const edges = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const tl = gray[((y - 1 + h) % h) * w + ((x - 1 + w) % w)];
      const t  = gray[((y - 1 + h) % h) * w + x];
      const tr = gray[((y - 1 + h) % h) * w + ((x + 1) % w)];
      const l  = gray[y * w + ((x - 1 + w) % w)];
      const r  = gray[y * w + ((x + 1) % w)];
      const bl = gray[((y + 1) % h) * w + ((x - 1 + w) % w)];
      const bm = gray[((y + 1) % h) * w + x];
      const br = gray[((y + 1) % h) * w + ((x + 1) % w)];
      const gx = (tr + 2 * r + br) - (tl + 2 * l + bl);
      const gy = (bl + 2 * bm + br) - (tl + 2 * t + tr);
      edges[y * w + x] = Math.min(1, Math.sqrt(gx * gx + gy * gy) * edgeSens);
    }
  }

  // Step 3: Gaussian blur the edge map to create soft AO halos
  const blurR = config.blurRadius ?? 3;
  const blurred = new Float32Array(w * h);
  const sigma = blurR / 2.0;
  // Build 1D Gaussian kernel
  const kSize = blurR * 2 + 1;
  const kernel = new Float32Array(kSize);
  let kSum = 0;
  for (let i = 0; i < kSize; i++) {
    const x = i - blurR;
    kernel[i] = Math.exp(-(x * x) / (2 * sigma * sigma));
    kSum += kernel[i];
  }
  for (let i = 0; i < kSize; i++) kernel[i] /= kSum;

  // Horizontal pass
  const temp = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let k = 0; k < kSize; k++) {
        const sx = Math.min(w - 1, Math.max(0, x + k - blurR));
        acc += edges[y * w + sx] * kernel[k];
      }
      temp[y * w + x] = acc;
    }
  }
  // Vertical pass
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let k = 0; k < kSize; k++) {
        const sy = Math.min(h - 1, Math.max(0, y + k - blurR));
        acc += temp[sy * w + x] * kernel[k];
      }
      blurred[y * w + x] = acc;
    }
  }

  // Step 4: Build AO map: white (1.0) = no occlusion, dark = occluded
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const outCtx = out.getContext('2d')!;
  const outImg = outCtx.createImageData(w, h);

  for (let i = 0; i < w * h; i++) {
    // AO = 1 - clamped edge amount (edges are crevices → darker)
    const ao = Math.min(1, Math.max(0, 1.0 - blurred[i]));
    const val = Math.floor(ao * 255);
    const idx = i * 4;
    outImg.data[idx]     = val;
    outImg.data[idx + 1] = val;
    outImg.data[idx + 2] = val;
    outImg.data[idx + 3] = 255;
  }
  outCtx.putImageData(outImg, 0, 0);

  const tex = new THREE.CanvasTexture(out);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

// ---- Material Factory (MeshStandardMaterial, cached) ----------------------

export type MatKey = 'brick' | 'concrete' | 'sand' | 'wood' | 'metal' | 'trim' | 'darkwall' | 'rusty' | 'floor2' | 'ramp';

export const materialCache = new Map<string, THREE.MeshStandardMaterial>();

const normalMapCache = new Map<string, THREE.CanvasTexture>();
const roughnessMapCache = new Map<string, THREE.CanvasTexture>();
const aoMapCache = new Map<string, THREE.CanvasTexture>();

// Normal map strength per material type
const normalStrengthMap: Record<MatKey, number> = {
  brick: 4.0, concrete: 2.0, sand: 1.0, wood: 3.0,
  metal: 3.0, trim: 1.0, darkwall: 1.2, rusty: 2.5,
  floor2: 1.5, ramp: 1.0,
};

// Phase 6: AO intensity per material
const aoIntensityMap: Record<MatKey, number> = {
  brick: 0.9, concrete: 0.6, sand: 0.4, wood: 0.9,
  metal: 0.5, trim: 0.3, darkwall: 0.8, rusty: 0.7,
  floor2: 0.6, ramp: 0.5,
};

// Phase 6: Hex tiling — applied to large-surface materials only
const hexTilingMaterials = new Set<MatKey>(['brick', 'concrete', 'sand', 'darkwall', 'floor2', 'ramp']);

// Phase 6: Detail normal assignment per material
type DetailNormalType = 'concrete' | 'rock' | 'metal' | 'wood';
const detailNormalAssignment: Record<MatKey, DetailNormalType | null> = {
  brick: 'concrete',
  concrete: 'concrete',
  sand: null,
  wood: 'wood',
  metal: 'metal',
  trim: null,
  darkwall: 'concrete',
  rusty: 'rock',
  floor2: 'concrete',
  ramp: 'concrete',
};

// Phase 6: Detail normal parameters per material
const detailNormalParams: Record<MatKey, { scale: number; strength: number; fadeStart: number; fadeEnd: number }> = {
  brick:    { scale: 10.0, strength: 0.5, fadeStart: 3.0, fadeEnd: 10.0 },
  concrete: { scale: 8.0,  strength: 0.4, fadeStart: 4.0, fadeEnd: 12.0 },
  sand:     { scale: 6.0,  strength: 0.3, fadeStart: 3.0, fadeEnd: 8.0  },
  wood:     { scale: 6.0,  strength: 0.6, fadeStart: 3.0, fadeEnd: 10.0 },
  metal:    { scale: 12.0, strength: 0.6, fadeStart: 4.0, fadeEnd: 14.0 },
  trim:     { scale: 8.0,  strength: 0.3, fadeStart: 3.0, fadeEnd: 8.0  },
  darkwall: { scale: 8.0,  strength: 0.4, fadeStart: 4.0, fadeEnd: 12.0 },
  rusty:    { scale: 7.0,  strength: 0.5, fadeStart: 3.0, fadeEnd: 10.0 },
  floor2:   { scale: 9.0,  strength: 0.4, fadeStart: 4.0, fadeEnd: 12.0 },
  ramp:     { scale: 8.0,  strength: 0.35, fadeStart: 4.0, fadeEnd: 12.0 },
};

/** Clear all material and texture caches. Call at the start of new scene creation to prevent stale material retention. */
export function clearMaterialCaches(): void {
  materialCache.clear();
  normalMapCache.clear();
  roughnessMapCache.clear();
  aoMapCache.clear();
}

function cloneCanvasTexture(source: THREE.CanvasTexture): THREE.CanvasTexture {
  const sourceCanvas = source.image as HTMLCanvasElement;
  const canvas = document.createElement('canvas');
  canvas.width = sourceCanvas.width;
  canvas.height = sourceCanvas.height;
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.drawImage(sourceCanvas, 0, 0);

  const clone = new THREE.CanvasTexture(canvas);
  clone.colorSpace = source.colorSpace;
  clone.flipY = source.flipY;
  clone.premultiplyAlpha = source.premultiplyAlpha;
  clone.unpackAlignment = source.unpackAlignment;
  clone.needsUpdate = true;
  return clone;
}

export function mat(key: MatKey, repeatX = 1, repeatY = 1, renderer?: THREE.WebGLRenderer): THREE.MeshStandardMaterial {
  const cacheKey = `${key}_${repeatX}_${repeatY}`;
  let m = materialCache.get(cacheKey);
  if (m) return m;

  const texGen: Record<MatKey, () => THREE.CanvasTexture> = {
    brick: makeBrickTexture,
    concrete: makeConcreteTexture,
    sand: makeSandTexture,
    wood: makeWoodTexture,
    metal: makeMetalTexture,
    trim: makeTrimTexture,
    darkwall: makeDarkWallTexture,
    rusty: makeRustyTexture,
    floor2: makeConcreteTexture,
    ramp: makeConcreteTexture,
  };

  const roughnessValues: Record<MatKey, number> = {
    brick: 0.88, concrete: 0.82, sand: 0.95, wood: 0.72,
    metal: 0.28, trim: 0.7, darkwall: 0.90, rusty: 0.75,
    floor2: 0.75, ramp: 0.8,
  };
  const metalnessValues: Record<MatKey, number> = {
    brick: 0.0, concrete: 0.0, sand: 0.0, wood: 0.0,
    metal: 0.7, trim: 0.05, darkwall: 0.0, rusty: 0.35,
    floor2: 0.0, ramp: 0.0,
  };
  const colorMap: Record<MatKey, number> = {
    brick: 0xd4a870, concrete: 0x9a9080, sand: 0xd0b070, wood: 0x8a6840,
    metal: 0x606a78, trim: 0xb8a480, darkwall: 0x7a7060, rusty: 0x944028,
    floor2: 0x908878, ramp: 0x9a9080,
  };

  // Color texture with anisotropic filtering
  const tex = cloneCanvasTexture(texGen[key]());
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeatX, repeatY);
  tex.anisotropy = renderer ? renderer.capabilities.getMaxAnisotropy() : 4;

  // Generate and cache normal map
  const baseKey = key === 'floor2' || key === 'ramp' ? 'concrete' : key;
  let normalTex = normalMapCache.get(baseKey);
  if (!normalTex) {
    const baseTex = texGen[key]();
    normalTex = generateNormalMap(baseTex.image as HTMLCanvasElement, normalStrengthMap[key]);
    normalMapCache.set(baseKey, normalTex);
  }
  const normalClone = cloneCanvasTexture(normalTex);
  normalClone.wrapS = normalClone.wrapT = THREE.RepeatWrapping;
  normalClone.repeat.set(repeatX, repeatY);
  normalClone.anisotropy = tex.anisotropy;

  // Generate and cache roughness map
  let roughTex = roughnessMapCache.get(baseKey);
  if (!roughTex) {
    const baseTex = texGen[key]();
    roughTex = generateRoughnessMap(baseTex.image as HTMLCanvasElement, roughnessValues[key], 0.3);
    roughnessMapCache.set(baseKey, roughTex);
  }
  const roughClone = cloneCanvasTexture(roughTex);
  roughClone.wrapS = roughClone.wrapT = THREE.RepeatWrapping;
  roughClone.repeat.set(repeatX, repeatY);

  // Phase 6: Generate and cache procedural AO map
  let aoTex = aoMapCache.get(baseKey);
  if (!aoTex) {
    const baseTex = texGen[key]();
    aoTex = generateProceduralAO(baseTex.image as HTMLCanvasElement, {
      intensity: aoIntensityMap[key],
      blurRadius: key === 'brick' || key === 'wood' ? 4 : 3,
      edgeSensitivity: key === 'brick' ? 4.0 : key === 'wood' ? 3.5 : 2.5,
    });
    aoMapCache.set(baseKey, aoTex);
  }
  const aoClone = cloneCanvasTexture(aoTex);
  aoClone.wrapS = aoClone.wrapT = THREE.RepeatWrapping;
  aoClone.repeat.set(repeatX, repeatY);

  m = new THREE.MeshStandardMaterial({
    map: tex,
    normalMap: normalClone,
    normalScale: new THREE.Vector2(1.0, 1.0),
    roughnessMap: roughClone,
    aoMap: aoClone,
    aoMapIntensity: aoIntensityMap[key],
    color: colorMap[key],
    roughness: roughnessValues[key],
    metalness: metalnessValues[key],
    envMapIntensity: key === 'metal' ? 2.0 : key === 'rusty' ? 1.0 : 0.6,
  });

  // Phase 6: Apply hex tiling (injects shader, must be before caching)
  if (hexTilingMaterials.has(key)) {
    applyHexTiling(m);
  }

  // Phase 6: Apply detail normal map via shader injection
  const detailType = detailNormalAssignment[key];
  if (detailType !== null) {
    let detailTex: THREE.CanvasTexture;
    switch (detailType) {
      case 'concrete': detailTex = getConcreteGrain();  break;
      case 'rock':     detailTex = getRockGrain();       break;
      case 'metal':    detailTex = getMetalScratch();    break;
      case 'wood':     detailTex = getWoodGrain();       break;
    }
    const p = detailNormalParams[key];
    addDetailNormal(m, detailTex!, p.scale, p.strength, p.fadeStart, p.fadeEnd);
  }

  materialCache.set(cacheKey, m);
  return m;
}

// ---- MatKey → PBR Texture Set Name Mapping --------------------------------

/** Maps each MatKey to the texture set name used by PBRTextureLoader, or null if no PBR set exists. */
export const matKeyToTextureSet: Record<MatKey, string | null> = {
  brick: 'brick-desert',
  darkwall: 'brick-dark',
  concrete: 'concrete-clean',
  floor2: 'concrete-weathered',
  ramp: 'concrete-stained',
  sand: 'sand-dirt',
  wood: 'wood-planks',
  metal: 'metal-clean',
  rusty: 'metal-rusty',
  trim: null,  // no PBR set, stays procedural
};

// ---- PBR Texture Upgrade (async, replaces procedural textures in-place) ----

/**
 * Parse the MatKey from a materialCache key string.
 * Cache keys have the format `${matKey}_${repeatX}_${repeatY}`.
 */
function parseCacheKey(cacheKey: string): { matKey: MatKey; repeatX: number; repeatY: number } | null {
  const lastUnder = cacheKey.lastIndexOf('_');
  if (lastUnder === -1) return null;
  const beforeLast = cacheKey.lastIndexOf('_', lastUnder - 1);
  if (beforeLast === -1) return null;

  const matKey = cacheKey.substring(0, beforeLast) as MatKey;
  const repeatX = parseFloat(cacheKey.substring(beforeLast + 1, lastUnder));
  const repeatY = parseFloat(cacheKey.substring(lastUnder + 1));

  if (!(matKey in matKeyToTextureSet) || isNaN(repeatX) || isNaN(repeatY)) return null;
  return { matKey, repeatX, repeatY };
}

/**
 * Clone a PBR texture with the correct repeat/wrap settings for a material slot.
 */
function cloneWithRepeat(
  source: THREE.Texture,
  repeatX: number,
  repeatY: number,
  anisotropy = 4,
): THREE.Texture {
  const cloned = source.clone();
  cloned.wrapS = cloned.wrapT = THREE.RepeatWrapping;
  cloned.repeat.set(repeatX, repeatY);
  cloned.anisotropy = anisotropy;
  cloned.needsUpdate = true;
  return cloned;
}

/**
 * Upgrade all cached materials from procedural textures to real PBR texture sets.
 *
 * @returns Number of materials that were upgraded.
 */
export async function upgradeMaterials(loader: PBRTextureLoader): Promise<number> {
  const textureSets: Map<string, PBRTextureSet> = await loader.loadAllSets();

  let upgraded = 0;

  for (const [cacheKey, material] of materialCache) {
    const parsed = parseCacheKey(cacheKey);
    if (!parsed) continue;

    const { matKey, repeatX, repeatY } = parsed;
    const setName = matKeyToTextureSet[matKey];
    if (setName === null) continue;

    const pbrSet = textureSets.get(setName);
    if (!pbrSet) continue;

    const anisotropy = material.map?.anisotropy ?? 4;

    const oldTextures: THREE.Texture[] = [];
    if (material.map) oldTextures.push(material.map);
    if (material.normalMap) oldTextures.push(material.normalMap);
    if (material.roughnessMap) oldTextures.push(material.roughnessMap);
    if (material.aoMap) oldTextures.push(material.aoMap);
    if (material.metalnessMap) oldTextures.push(material.metalnessMap);

    if (pbrSet.color) {
      material.map = cloneWithRepeat(pbrSet.color, repeatX, repeatY, anisotropy);
      material.color.setHex(0xffffff);
    }

    if (pbrSet.normal) {
      material.normalMap = cloneWithRepeat(pbrSet.normal, repeatX, repeatY, anisotropy);
    }

    if (pbrSet.orm) {
      // Single clone for all 3 ORM channels — Three.js reads R=AO, G=Roughness, B=Metalness
      const ormShared = cloneWithRepeat(pbrSet.orm, repeatX, repeatY, anisotropy);

      material.aoMap = ormShared;
      material.aoMapIntensity = 1.0;
      material.roughnessMap = ormShared;
      material.roughness = 1.0;
      material.metalnessMap = ormShared;
      material.metalness = 1.0;
    }

    material.needsUpdate = true;
    upgraded++;

    for (const oldTex of oldTextures) {
      oldTex.dispose();
    }
  }

  return upgraded;
}
